import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/layout/Layout';
import Header from '../components/layout/Header';
import KPICard from '../components/dashboard/KPICard';
import SortableKPICard from '../components/dashboard/SortableKPICard';
import TrendChart from '../components/dashboard/TrendChart';
import DataTable from '../components/dashboard/DataTable';
import NotesSection from '../components/dashboard/NotesSection';
import ActionItems from '../components/dashboard/ActionItems';
import ShareModal from '../components/dashboard/ShareModal';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Share2, ArrowUpDown, Check, X } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardTemplate = ({ 
  functionType, 
  title, 
  subtitle,
  icon: Icon 
}) => {
  const { selectedYear, periodType, selectedPeriod } = useApp();
  const { isAdmin, isELT } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  
  // Time-aware trends data
  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  
  // Delete KPI state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [kpiToDelete, setKpiToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Share modal state
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  
  // Add KPI state
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [availableKpis, setAvailableKpis] = useState([]);
  const [selectedKpiToAdd, setSelectedKpiToAdd] = useState('');
  
  // Comment counts state
  const [commentCounts, setCommentCounts] = useState({});
  
  // Reorder mode state
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [kpiOrder, setKpiOrder] = useState([]);
  const [originalOrder, setOriginalOrder] = useState([]);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchDashboard = useCallback(async () => {
    if (!selectedPeriod) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/dashboard/${functionType}`, {
        params: { 
          year: selectedYear, 
          period_type: periodType,
          period_start: selectedPeriod?.startDate,
          period_end: selectedPeriod?.endDate
        }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [functionType, selectedYear, periodType, selectedPeriod]);

  // Fetch time-aware trend data
  const fetchTrendData = useCallback(async () => {
    if (!selectedPeriod) return;
    
    setTrendLoading(true);
    try {
      const response = await axios.get(`${API}/dashboard/${functionType}/trends`, {
        params: { 
          year: selectedYear, 
          period_type: periodType,
          period_start: selectedPeriod?.startDate,
          period_end: selectedPeriod?.endDate
        }
      });
      setTrendData(response.data);
    } catch (error) {
      console.error('Failed to fetch trend data:', error);
      setTrendData(null);
    } finally {
      setTrendLoading(false);
    }
  }, [functionType, selectedYear, periodType, selectedPeriod]);

  // Fetch comment counts for all KPIs in the current period
  const fetchCommentCounts = useCallback(async () => {
    if (!selectedPeriod) return;
    
    try {
      const response = await axios.get(`${API}/comments/count`, {
        params: {
          function: functionType,
          year: selectedYear,
          period_type: periodType,
          period_start_date: selectedPeriod?.startDate
        }
      });
      setCommentCounts(response.data.counts || {});
    } catch (error) {
      console.error('Failed to fetch comment counts:', error);
      setCommentCounts({});
    }
  }, [functionType, selectedYear, periodType, selectedPeriod]);

  useEffect(() => {
    fetchDashboard();
    fetchTrendData();
    fetchCommentCounts();
  }, [fetchDashboard, fetchTrendData, fetchCommentCounts]);

  // Handle KPI deletion
  const handleDeleteKpi = (kpiId, kpiName) => {
    setKpiToDelete({ id: kpiId, name: kpiName });
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteKpi = async () => {
    if (!kpiToDelete) return;
    
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/kpi-definitions/${kpiToDelete.id}`);
      toast.success(`"${kpiToDelete.name}" removed from dashboard`);
      setIsDeleteDialogOpen(false);
      setKpiToDelete(null);
      fetchDashboard(); // Refresh dashboard
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove KPI');
    } finally {
      setIsDeleting(false);
    }
  };

  // === REORDER FUNCTIONALITY ===
  // Initialize KPI order when dashboard data changes
  useEffect(() => {
    if (dashboardData?.kpis) {
      const order = dashboardData.kpis.map(kpi => kpi.definition_id || kpi.metric_name);
      setKpiOrder(order);
      setOriginalOrder(order);
    }
  }, [dashboardData?.kpis]);

  // Enter reorder mode
  const handleEnterReorderMode = () => {
    setIsReorderMode(true);
    setOriginalOrder([...kpiOrder]);
  };

  // Cancel reorder mode
  const handleCancelReorder = () => {
    setKpiOrder([...originalOrder]);
    setIsReorderMode(false);
  };

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id && over) {
      setKpiOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Save new order
  const handleSaveOrder = async () => {
    setIsSavingOrder(true);
    try {
      // Build updates array with new sort_order values
      const updates = kpiOrder.map((id, index) => ({
        id: id,
        sort_order: (index + 1) * 10 // Use increments of 10 for easier insertion later
      }));

      await axios.post(`${API}/kpi-definitions/bulk-update-order`, updates);
      toast.success('KPI order saved');
      setIsReorderMode(false);
      setOriginalOrder([...kpiOrder]);
      // Refresh dashboard to get updated order from backend
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to save order');
      console.error('Save order error:', error);
    } finally {
      setIsSavingOrder(false);
    }
  };

  // Get sorted KPIs based on current order
  const getSortedKpis = () => {
    if (!dashboardData?.kpis) return [];
    
    return [...dashboardData.kpis].sort((a, b) => {
      const aId = a.definition_id || a.metric_name;
      const bId = b.definition_id || b.metric_name;
      return kpiOrder.indexOf(aId) - kpiOrder.indexOf(bId);
    });
  };
  // === END REORDER FUNCTIONALITY ===

  // Handle adding KPI to dashboard
  const fetchAvailableKpis = async () => {
    try {
      // Get archived KPIs for this function
      const response = await axios.get(`${API}/kpi-definitions/history`, {
        params: { function: functionType }
      });
      setAvailableKpis(response.data);
    } catch (error) {
      console.error('Failed to fetch archived KPIs:', error);
      setAvailableKpis([]);
    }
  };

  const openAddDialog = () => {
    fetchAvailableKpis();
    setSelectedKpiToAdd('');
    setIsAddDialogOpen(true);
  };

  const handleRestoreKpi = async () => {
    if (!selectedKpiToAdd) {
      toast.error('Please select a KPI to restore');
      return;
    }
    
    try {
      await axios.post(`${API}/kpi-definitions/${selectedKpiToAdd}/restore`);
      toast.success('KPI restored to dashboard');
      setIsAddDialogOpen(false);
      setSelectedKpiToAdd('');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore KPI');
    }
  };

  const getRAGStatus = (kpi) => {
    if (!kpi.latest?.plan_value) return 'no_plan';
    const variance = (kpi.latest.actual_value - kpi.latest.plan_value) / Math.abs(kpi.latest.plan_value);
    const isLowerBetter = kpi.latest.directionality === 'lower_is_better';
    const adjustedVariance = isLowerBetter ? -variance : variance;
    
    if (adjustedVariance >= 0) return 'green';
    if (adjustedVariance >= -0.05) return 'yellow';
    return 'red';
  };

  const prepareChartData = () => {
    if (!dashboardData?.kpis) return [];
    
    const dateMap = {};
    dashboardData.kpis.forEach(kpi => {
      kpi.trend?.forEach(point => {
        if (!dateMap[point.date]) {
          dateMap[point.date] = { date: point.date };
        }
        dateMap[point.date][kpi.metric_name] = point.value;
      });
    });
    
    return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
  };

  const tableColumns = [
    { key: 'metric_name', label: 'Metric', sortable: true },
    { key: 'actual_value', label: 'Actual', type: 'number', sortable: true },
    { key: 'plan_value', label: 'Plan', type: 'number', sortable: true },
    { key: 'variance', label: 'Variance', type: 'percentage', sortable: true },
    { key: 'rag_status', label: 'Status', type: 'status' },
    { key: 'owner', label: 'Owner' },
  ];

  const tableData = dashboardData?.kpis?.map(kpi => ({
    metric_name: kpi.metric_name,
    actual_value: kpi.has_data ? kpi.latest?.actual_value : null,
    plan_value: kpi.has_data ? kpi.latest?.plan_value : null,
    variance: kpi.has_data && kpi.variance ? kpi.variance * 100 : null,
    rag_status: kpi.rag_status,
    owner: kpi.has_data ? (kpi.latest?.owner || '—') : '—',
    has_data: kpi.has_data,
  })) || [];

  if (loading) {
    return (
      <Layout>
        <Header title={title} subtitle={subtitle} />
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-28 sm:h-36 rounded-lg" />
            ))}
          </div>
          <Skeleton className="h-56 sm:h-72 rounded-lg" />
          <Skeleton className="h-48 sm:h-64 rounded-lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header title={title} subtitle={selectedPeriod?.label || subtitle} />
      
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6" data-testid={`${functionType}-dashboard`}>
        {/* KPI Cards Header with Share, Reorder, and Add Buttons */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[#111827]">Key Metrics</h2>
          <div className="flex items-center gap-2">
            {isReorderMode ? (
              <>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleCancelReorder}
                  data-testid="cancel-reorder-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleSaveOrder}
                  disabled={isSavingOrder}
                  data-testid="save-order-btn"
                >
                  <Check className="w-4 h-4 mr-1" />
                  {isSavingOrder ? 'Saving...' : 'Save Order'}
                </Button>
              </>
            ) : (
              <>
                {isAdmin() && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleEnterReorderMode}
                    data-testid="reorder-btn"
                  >
                    <ArrowUpDown className="w-4 h-4 mr-1" />
                    Reorder
                  </Button>
                )}
                {isAdmin() && (
                  <>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setIsShareModalOpen(true)}
                      data-testid="share-btn"
                    >
                      <Share2 className="w-4 h-4 mr-1" />
                      Share
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={openAddDialog}
                      data-testid="add-kpi-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Restore KPI
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Reorder Mode Instructions */}
        {isReorderMode && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
            <ArrowUpDown className="w-5 h-5 text-blue-600" />
            <span className="text-sm text-blue-800">
              Drag the handle on the left of each card to reorder. Click "Save Order" when done.
            </span>
          </div>
        )}
        
        {/* KPI Cards - Sortable Grid */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={kpiOrder} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 sm:gap-4 items-start">
              {getSortedKpis().map((kpi) => (
                <SortableKPICard
                  key={kpi.definition_id || kpi.metric_name}
                  id={kpi.definition_id || kpi.metric_name}
                  isReorderMode={isReorderMode}
                  title={kpi.metric_name}
                  value={kpi.has_data ? kpi.latest?.actual_value : null}
                  unit={kpi.unit || kpi.latest?.unit}
                  planValue={kpi.has_data ? kpi.latest?.plan_value : null}
                  variance={kpi.has_data ? kpi.variance : null}
                  ragStatus={kpi.rag_status}
                  trend={kpi.trend}
                  yoy={kpi.yoy}
                  kpiId={kpi.definition_id}
                  onDelete={handleDeleteKpi}
                  canEdit={isELT() && !isReorderMode}
                  // Period context for inline editing
                  periodType={periodType}
                  periodStartDate={selectedPeriod?.startDate}
                  periodEndDate={selectedPeriod?.endDate}
                  year={selectedYear}
                  functionName={functionType}
                  onDataUpdate={fetchDashboard}
                  // Last updated info
                  updatedBy={kpi.latest?.updated_by_name || kpi.latest?.uploaded_by_name}
                  updatedAt={kpi.latest?.updated_at}
                  // Weekly pacing props
                  isPacedWeekly={kpi.is_paced_weekly}
                  pacing={kpi.pacing}
                  actualToDate={kpi.actual_to_date}
                  expectedToDate={kpi.expected_to_date}
                  monthlyPlan={kpi.monthly_plan}
                  weeklyPlan={kpi.weekly_plan}
                  paceStatus={kpi.pace_status}
                  pacingPercentage={kpi.pacing_percentage}
                  // Segment breakdown (Brokerage only)
                  segmentBreakdown={kpi.segment_breakdown}
                  // Comment count
                  commentCount={commentCounts[kpi.metric_name] || 0}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {/* Trend Chart - Time-Aware */}
        {trendLoading ? (
          <Skeleton className="h-56 sm:h-72 rounded-lg" />
        ) : (
          <TrendChart
            data={trendData?.trend_data || prepareChartData()}
            metrics={trendData?.metrics || dashboardData?.kpis?.map(k => k.metric_name) || []}
            title={`${title} Trends`}
            viewGranularity={trendData?.view_granularity || 'monthly'}
            periodType={periodType}
          />
        )}

        {/* Tabs Section */}
        <Tabs defaultValue="details" className="w-full">
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="details" className="flex-1 sm:flex-none">Details</TabsTrigger>
            <TabsTrigger value="notes" className="flex-1 sm:flex-none">Notes & Actions</TabsTrigger>
          </TabsList>
          
          <TabsContent value="details" className="mt-4">
            <DataTable
              columns={tableColumns}
              data={tableData}
              title="KPI Details"
            />
          </TabsContent>
          
          <TabsContent value="notes" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <NotesSection
                notes={dashboardData?.notes}
                functionType={functionType}
                periodType={periodType}
                year={selectedYear}
                periodStart={selectedPeriod?.startDate}
                periodEnd={selectedPeriod?.endDate}
                onNoteAdded={fetchDashboard}
              />
              <ActionItems
                actions={dashboardData?.action_items}
                functionType={functionType}
                periodType={periodType}
                year={selectedYear}
                onActionUpdated={fetchDashboard}
              />
            </div>
          </TabsContent>
        </Tabs>

        {/* Last Updated */}
        {dashboardData?.last_updated && (
          <p className="text-xs sm:text-sm text-[#9CA3AF] text-right">
            Last updated: {new Date(dashboardData.last_updated).toLocaleString()}
          </p>
        )}
      </div>

      {/* Delete KPI Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-rose-600">Remove KPI from Dashboard</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove "{kpiToDelete?.name}" from the dashboard? 
              The KPI data will be preserved and can be restored later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeleteKpi}
              disabled={isDeleting}
              data-testid="confirm-delete-kpi-btn"
            >
              {isDeleting ? 'Removing...' : 'Remove KPI'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore KPI Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Restore KPI to Dashboard</DialogTitle>
            <DialogDescription>
              Select an archived KPI to restore to this dashboard.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {availableKpis.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">
                No archived KPIs available for this department.
              </p>
            ) : (
              <Select value={selectedKpiToAdd} onValueChange={setSelectedKpiToAdd}>
                <SelectTrigger data-testid="select-kpi-to-restore">
                  <SelectValue placeholder="Select a KPI to restore" />
                </SelectTrigger>
                <SelectContent>
                  {availableKpis.map((kpi) => (
                    <SelectItem key={kpi.id} value={kpi.id}>
                      {kpi.metric_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRestoreKpi} 
              disabled={!selectedKpiToAdd || availableKpis.length === 0}
              className="bg-[#2563EB]"
              data-testid="confirm-restore-kpi-btn"
            >
              Restore KPI
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Modal */}
      <ShareModal
        open={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        dashboardType={functionType}
        title={title}
      />
    </Layout>
  );
};

export default DashboardTemplate;
